import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, ListSubheader } from '@mui/material';

import { Loading, Menu } from '@components/base';
import useChannelsQuery from '@hooks/queryHooks/useChannelsQuery';
import useProvidersQueries from '@hooks/queryHooks/useProvidersQueries';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { filterProviders } from '@utils/providerUtils';

import ToolbarButton from './ToolbarButton';

const SearchToolbarProviderSelector: FC = () => {
  const { t } = useTranslation();
  const { data: providers } = useProvidersQueries();
  const { data: channels } = useChannelsQuery();

  const {
    isReadonly,
    provider,
    updateProvider,
    filterDefLoad: { isLoading: filterDefsLoading, data: filterDefs },
  } = useSearchAndFilter();

  const filteredProviders = useMemo(() => filterProviders(providers), [providers]);

  return (
    <Menu>
      <ToolbarButton
        disabled={isReadonly}
        isLastItem
        isMenuButton
        rightIcon='chevronDown'
        subtitle={
          provider &&
          (providers?.find(p => p.id === provider)?.name ??
            channels?.data.find(c => c.value.id === provider)?.value.name)
        }
        title={t('provider')}
      />

      <Menu.Content>
        {filterDefsLoading || !filterDefs ? (
          <Box alignItems='center' display='flex' justifyContent='center' minWidth={160} p={4}>
            <CircularProgress />
          </Box>
        ) : (
          [
            <Menu.Item divider key='none' onClick={() => updateProvider()} selected={!provider}>
              {t('none')}
            </Menu.Item>,

            <ListSubheader key='providers'>{t('providers')}</ListSubheader>,

            ...(filteredProviders.length > 0
              ? filteredProviders.map(p => (
                  <Menu.Item
                    key={p.id}
                    onClick={() => updateProvider(p.id)}
                    selected={provider === p.id}
                    title={p.id}
                  >
                    {p.name}
                  </Menu.Item>
                ))
              : [<Loading BoxProps={{ p: 2 }} key='providers__loader' />]),

            <ListSubheader key='channels'>{t('channels')}</ListSubheader>,

            ...(channels
              ? channels.data.map(channel => (
                  <Menu.Item
                    key={channel.value.id}
                    onClick={() => updateProvider(channel.value.id)}
                    selected={provider === channel.value.id}
                    title={channel.value.id}
                  >
                    {channel.value.name}
                  </Menu.Item>
                ))
              : [<Loading BoxProps={{ p: 2 }} key='channels__loader' />]),
          ]
        )}
      </Menu.Content>
    </Menu>
  );
};

export default SearchToolbarProviderSelector;
